/**
 * ConstraintGlyphLayer (Faz 6.7, OCS glyph UI parity): every enabled
 * constraint renders as a screen-space pill at its anchor point — fixed
 * pixel size regardless of zoom (GLYPH_SIZE 14 + pad), shared anchors fan
 * out along the screen X axis. Clicking a glyph selects it (the
 * DELCONSTRAINT target); conflicting constraints render red. Positioning
 * is recomputed per frame through refs (no React re-render per frame);
 * only the transform is an inline style (runtime value contract).
 */

import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { Box } from '@chakra-ui/react';
import { useAppStore } from '../state/store';
import { useConstraintStore } from '../core/constraints/constraintStore';
import { computeGlyphPlacements, GLYPH_SIZE } from '../core/constraints/glyphs';

export function ConstraintGlyphLayer() {
    const entities = useAppStore((s) => s.entities);
    const set = useConstraintStore((s) => s.set);
    const selectedId = useConstraintStore((s) => s.selectedConstraintId);
    const setSelected = useConstraintStore((s) => s.setSelectedConstraint);
    const { camera, size } = useThree();

    const visible = useMemo(
        () => computeGlyphPlacements(entities, set).filter((p) => !p.hidden),
        [entities, set]
    );
    const nodeRefs = useRef(new Map<string, HTMLDivElement>());
    const scratch = useRef(new THREE.Vector3());

    useFrame(() => {
        const v = scratch.current;
        for (const p of visible) {
            const el = nodeRefs.current.get(p.constraintId);
            if (!el) continue;
            v.set(p.anchor[0], p.anchor[1], p.anchor[2] ?? 0).project(camera);
            const x = ((v.x + 1) / 2) * size.width + p.offsetX;
            const y = ((-v.y + 1) / 2) * size.height;
            el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
            el.style.visibility = v.z < 1 ? 'visible' : 'hidden';
        }
    });

    if (visible.length === 0) return null;

    return (
        <Html
            fullscreen
            prepend
            zIndexRange={[20, 10]}
            style={{ pointerEvents: 'none', overflow: 'hidden' }}
        >
            {visible.map((p) => (
                <Box
                    key={p.constraintId}
                    ref={(el: HTMLDivElement | null) => {
                        if (el) nodeRefs.current.set(p.constraintId, el);
                        else nodeRefs.current.delete(p.constraintId);
                    }}
                    position="absolute"
                    top="0"
                    left="0"
                    h={`${GLYPH_SIZE + 4}px`}
                    px="6px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    pointerEvents="auto"
                    cursor="pointer"
                    fontFamily="mono"
                    fontSize="10px"
                    fontWeight="bold"
                    lineHeight="1"
                    whiteSpace="nowrap"
                    borderRadius="7px"
                    bg="bg.panel"
                    border="1px solid"
                    borderColor={p.conflict ? 'red.400' : selectedId === p.constraintId ? 'brand.500' : 'border.base'}
                    color={p.conflict ? 'red.400' : 'text.primary'}
                    boxShadow="sm"
                    title={`${p.constraintId}: ${p.tooltip}`}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setSelected(selectedId === p.constraintId ? null : p.constraintId);
                    }}
                >
                    {p.label}
                </Box>
            ))}
        </Html>
    );
}
